import { action, computed, observable } from 'mobx';
import { CourseMetadata, VocabularyItem, VocabularyNamespaces } from 'types';
import { CourseInfo, DynamicCourseInfo } from 'types/course';

import CatalogStore from 'stores/Catalog';
import UIStore from 'stores/UI';
import VocabularyStore from 'stores/Vocabulary';
import ProgressStore from 'stores/Progress';
import UserStore from 'stores/User';

import {
  getCourseInfoMeta,
  getCourseInfo as getDynamicCourseInfo,
} from 'services/ClassroomService';
import { getCourseMetadata } from 'services/CourseService';
import { getCourseEnrollment } from 'services/UserEnrollmentService';
import { getPremiumSessionCollateral } from 'services/PremiumSessionService';
import { PremiumSessionCollateral } from 'types/premiumSession';
import { LabGradeResults } from 'types/labs';
import { getLabGradeResultsByCourseSlug } from 'services/LabGradeResultsService';
import FeaturesStore from 'stores/Features';
import { EnablementFeatureFlags } from 'config/featureFlags';

class CourseLandingStore {
  @observable isLoading = true;

  @observable isDataLoaded = false;

  @observable courseSlug: string = null;

  @observable courseInfo: CourseInfo = null;

  @observable dynamicCourseInfo: DynamicCourseInfo = null;

  @observable courseCode: string = null;

  @observable courseVersion: string = null;

  @observable courseMetadata: CourseMetadata = null;

  @observable coursePremiumSessionCollateral: PremiumSessionCollateral = null;

  @observable courseProgress: number = null;

  @observable translations: string[] = null;

  @observable languageOverride: string;

  @observable versions: string[] = [];

  @observable labGradeResults: LabGradeResults = null;

  // vocabulary
  @observable productVocabulary: VocabularyItem[] = null;

  @observable audienceVocabulary: VocabularyItem[] = null;

  @observable topicVocabulary: VocabularyItem[] = null;

  // error handling
  @observable isError = false;

  /**
   * true when the user does not have an enrollment for the course
   */
  @observable isInaccessible = false;

  @observable isPremiumSessionCourseWithoutEntry: boolean = false;

  uiStore: UIStore;

  catalogStore: CatalogStore;

  vocabularyStore: VocabularyStore;

  progressStore: ProgressStore;

  featuresStore: FeaturesStore;

  userStore: UserStore;

  constructor(
    uiStore: UIStore,
    catalogStore: CatalogStore,
    vocabularyStore: VocabularyStore,
    progressStore: ProgressStore,
    userStore: UserStore,
    featuresStore: FeaturesStore,
    isPremiumSessionCourseWithoutEntry: boolean,
  ) {
    this.uiStore = uiStore;
    this.catalogStore = catalogStore;
    this.vocabularyStore = vocabularyStore;
    this.progressStore = progressStore;
    this.userStore = userStore;
    this.featuresStore = featuresStore;
    this.isPremiumSessionCourseWithoutEntry =
      isPremiumSessionCourseWithoutEntry;
  }

  @computed get language() {
    return this.languageOverride || this.uiStore.currentLanguage || 'en-US';
  }

  @computed get title() {
    if (this.isPremiumSessionCourseWithoutEntry)
      return this.coursePremiumSessionCollateral?.offering_title;
    if (this.currentCourse) return this.currentCourse.title;

    return null;
  }

  @computed get currentVersion() {
    return this.currentCourse ? this.currentCourse.version : null;
  }

  @computed get currentCourse() {
    return CourseLandingStore.getCurrentCourse(
      this.dynamicCourseInfo,
      this.language,
    );
  }

  @computed get productLabels() {
    if (
      this.courseMetadata &&
      this.courseMetadata.taxonomies?.products &&
      this.productVocabulary
    ) {
      const labels = this.courseMetadata.taxonomies.products.flatMap((p) => {
        const vocabLabel = this.productVocabulary.find((v) => v.token === p);
        return vocabLabel ? [vocabLabel.display_name] : [];
      });

      return labels;
    }

    return null;
  }

  @computed get topicLabels() {
    if (
      this.courseMetadata &&
      this.courseMetadata.taxonomies?.topics &&
      this.topicVocabulary
    ) {
      const labels = this.courseMetadata.taxonomies.topics.flatMap((p) => {
        const vocabLabel = this.topicVocabulary.find((v) => v.token === p);
        return vocabLabel ? [vocabLabel.display_name] : [];
      });

      return labels;
    }

    return null;
  }

  @computed get audienceLabels() {
    if (
      this.courseMetadata &&
      this.courseMetadata.taxonomies?.audiences &&
      this.audienceVocabulary
    ) {
      const labels = this.courseMetadata.taxonomies.audiences.flatMap((p) => {
        const vocabLabel = this.audienceVocabulary.find((v) => v.token === p);
        return vocabLabel ? [vocabLabel.display_name] : [];
      });

      return labels;
    }

    return null;
  }

  @action fetchOverviewData = async (code: string, version: string) => {
    try {
      this.isLoading = true;

      const slug = `${code}-${version}`;

      this.courseSlug = slug;

      if (this.isPremiumSessionCourseWithoutEntry) {
        // case no catalog entry exists for the course, skip to load premium session collateral
        const sessionSlug = `${slug}-s01`;

        const coursePremiumSessionCollateral =
          await getPremiumSessionCollateral(
            sessionSlug,
            this.language,
            true,
            'en-US', // fallback to english if fails
          );

        this.coursePremiumSessionCollateral = coursePremiumSessionCollateral;
      } else {
        const dynamicCourseInfo = await getDynamicCourseInfo(slug);

        if (!dynamicCourseInfo || !dynamicCourseInfo.dynamic_info) {
          throw new Error(`Dynamic course info not found, courseSlug: ${slug}`);
        }

        this.dynamicCourseInfo = dynamicCourseInfo.dynamic_info;

        this.courseCode = this.dynamicCourseInfo.code;
        this.courseVersion = this.dynamicCourseInfo.current;
        this.translations =
          this.dynamicCourseInfo.variants?.[this.dynamicCourseInfo.current] ||
          [];
        this.versions = Object.keys(this.dynamicCourseInfo.variants || [])
          .map((a) =>
            a
              .split('.')
              .map((n) => +n + 100000)
              .join('.'),
          )
          .sort((a, b) => parseFloat(b) - parseFloat(a))
          .map((a) =>
            a
              .split('.')
              .map((n) => +n - 100000)
              .join('.'),
          );
        this.uiStore.reportActiveCourseTranslations(this.translations);

        // not saving enrollment, only checking
        try {
          await getCourseEnrollment(this.courseSlug, this.userStore.username);
        } catch (err) {
          this.isInaccessible = true;
        }

        // should continue without course metadata
        // to prevent error screen
        try {
          const courseMetadata = await getCourseMetadata(this.courseSlug);
          this.courseMetadata = courseMetadata;
        } catch (courseMetadataError) {
          this.courseMetadata = null;
        }

        // can continue without course info
        try {
          const courseInfo = await getCourseInfoMeta(this.courseSlug);

          this.courseInfo = courseInfo;
        } catch (courseInfoError) {
          this.courseInfo = null;
        }

        const productVocabulary =
          await this.vocabularyStore.getVocabularyByNamespace(
            VocabularyNamespaces.OfferingTaxonomyProducts,
            this.language,
          );
        if (productVocabulary && productVocabulary.length) {
          this.productVocabulary = productVocabulary;
        }

        const audienceVocabulary =
          await this.vocabularyStore.getVocabularyByNamespace(
            VocabularyNamespaces.OfferingTaxonomyAudiences,
            this.language,
          );
        if (audienceVocabulary && audienceVocabulary.length) {
          this.audienceVocabulary = audienceVocabulary;
        }

        const topicVocabulary =
          await this.vocabularyStore.getVocabularyByNamespace(
            VocabularyNamespaces.OfferingTaxonomyTopics,
            this.language,
          );
        if (topicVocabulary && topicVocabulary.length) {
          this.topicVocabulary = topicVocabulary;
        }

        const courseProgress = this.progressStore.getProgressForCourse(slug);

        if (courseProgress && courseProgress.total_progress) {
          this.courseProgress = courseProgress.total_progress;
        }

        const isLabNotificationEnabled = this.featuresStore.isFeatureEnabled(
          'enablements',
          EnablementFeatureFlags.LabNotificationEnabled,
          false,
        );

        if (isLabNotificationEnabled) {
          try {
            const labGradeResults = await getLabGradeResultsByCourseSlug(slug);
            this.labGradeResults = labGradeResults;
          } catch (labGradeResultsError) {
            this.labGradeResults = null;
          }
        }
      }

      this.isDataLoaded = true;
      this.isLoading = false;
    } catch (err) {
      this.isError = true;
      this.isLoading = false;

      throw new Error(err);
    }
  };

  // to be used when the arguments for fetchOverviewData
  // can't be processed or an error state is to be invoked
  // due to some external logic
  @action externalLoadingError = () => {
    if (!this.isError) {
      this.isError = true;
      this.isDataLoaded = false;
      this.isLoading = false;
    }
  };

  static getCurrentCourse(dynamicInfo: DynamicCourseInfo, language = 'en-US') {
    const found = dynamicInfo?.summaries.find(
      (course) =>
        course.version === dynamicInfo.current && course.language === language,
    );

    const defaultCourse = dynamicInfo?.summaries.find(
      (course) =>
        course.version === dynamicInfo.current && course.language === 'en-US',
    );

    return found || defaultCourse || null;
  }
}

export default CourseLandingStore;
